import React, { useState, useMemo } from 'react';
import { MapPin, Calendar, Factory, Play, Phone, Mail } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { installationGallery } from '../mock/mockData';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import InternalLinks from '../components/seo/InternalLinks';

const InstallationGalleryPage = () => {
  const [selectedState, setSelectedState] = useState('All');
  
  const states = useMemo(() => 
    ['All', ...new Set(installationGallery.map(project => project.state))],
    [installationGallery]
  );
  
  const filteredProjects = useMemo(() => 
    selectedState === 'All' 
      ? installationGallery 
      : installationGallery.filter(p => p.state === selectedState),
    [selectedState, installationGallery]
  );

  const stateProjectCounts = useMemo(() => {
    const counts = {};
    states.filter(s => s !== 'All').forEach(state => {
      counts[state] = installationGallery.filter(p => p.state === state).length;
    });
    return counts;
  }, [states, installationGallery]);

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="Installation Gallery | Water Plant Projects Across India | Alden Technologies"
        description="Browse real photos and videos of Alden Technologies water bottling plant installations across India — filling machines, RO plants and complete turnkey lines in action."
        path="/installation-gallery"
      />
      <Breadcrumbs items={[{ label: 'Installation Gallery' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 px-6 py-2 rounded-full text-sm font-semibold mb-6">
            Our Success Stories
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Installation Gallery
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 mb-8">
            Explore our successful project installations across India. From small-scale startups to large industrial plants, we&apos;ve delivered excellence everywhere.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-center">
            <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-xl">
              <div className="text-3xl font-bold">100+</div>
              <div className="text-white/80">Projects Completed</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-xl">
              <div className="text-3xl font-bold">15+</div>
              <div className="text-white/80">States Covered</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-8 py-4 rounded-xl">
              <div className="text-3xl font-bold">95%</div>
              <div className="text-white/80">Client Retention</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {states.map(state => (
              <button
                key={state}
                onClick={() => setSelectedState(state)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  selectedState === state
                    ? 'bg-alden-green text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {state}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map(project => (
              <Card key={project.id} className="overflow-hidden hover:shadow-2xl transition-all group">
                <div className="relative">
                  <img 
                    src={project.image} 
                    alt={`${project.client} - ${project.city}`}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-alden-green text-white px-4 py-2 rounded-full font-bold shadow-lg">
                    {project.capacity}
                  </div>
                  {project.videoUrl && (
                    <a 
                      href={project.videoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <div className="bg-white rounded-full p-6">
                        <Play className="w-12 h-12 text-alden-green" />
                      </div>
                    </a>
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-alden-green font-semibold mb-3">
                    <MapPin className="w-5 h-5" />
                    <span>{project.city}, {project.state}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {project.client}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {project.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{project.year}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-500">
                      <Factory className="w-4 h-4" />
                      <span className="text-sm font-semibold">{project.capacity}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* State-wise Coverage Map */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Pan India Presence
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Successfully delivered turnkey mineral water bottling plants across major states in India.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {states.filter(s => s !== 'All').map(state => {
              const projectCount = stateProjectCounts[state];
              return (
                <Card key={state} className="text-center border-2 border-alden-green/20 hover:border-alden-green transition-all">
                  <CardContent className="p-6">
                    <MapPin className="w-12 h-12 text-alden-green mx-auto mb-3" />
                    <h3 className="font-bold text-lg mb-1">{state}</h3>
                    <p className="text-gray-600 text-sm">{projectCount} {projectCount === 1 ? 'Project' : 'Projects'}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client Testimonials from Projects */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Hear from business owners who trusted Alden Technologies for their complete turnkey plant solutions.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                id: 'test-rajesh',
                name: 'Rajesh Kumar',
                company: 'Rajasthan Pure Water Pvt Ltd',
                location: 'Jaipur',
                quote: 'Alden Technologies delivered our 60 BPM plant on time with excellent quality. Their after-sales support is outstanding.'
              },
              {
                id: 'test-amit',
                name: 'Amit Patel',
                company: 'Gujarat Hydro Industries',
                location: 'Ahmedabad',
                quote: 'Professional team, high-quality equipment, and seamless installation. Highly recommend for large-scale projects.'
              },
              {
                id: 'test-priya',
                name: 'Priya Sharma',
                company: 'UP Aqua Solutions',
                location: 'Noida',
                quote: 'The 90 BPM plant has been running flawlessly for over a year. Great engineering and technical support.'
              }
            ].map((testimonial) => (
              <Card key={testimonial.id} className="bg-white border-2 border-alden-green/20">
                <CardContent className="p-8">
                  <div className="text-5xl text-alden-green mb-4">&ldquo;</div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">
                    {testimonial.quote}
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-alden-green rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.company}</div>
                      <div className="text-xs text-alden-green">{testimonial.location}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Join 100+ successful businesses who trusted Alden Technologies for their mineral water bottling plants.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-alden-green hover:bg-gray-100 py-6 px-8 text-lg font-semibold">
                <Mail className="mr-2" />
                Request Free Quotation
              </Button>
            </Link>
            <a href="https://wa.me/919351292512?text=I%20want%20to%20discuss%20a%20mineral%20water%20plant%20project" target="_blank" rel="noopener noreferrer">
              <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-8 text-lg font-semibold">
                <Phone className="mr-2" />
                WhatsApp: +91 9351292512
              </Button>
            </a>
          </div>
        </div>
      </section>
      <InternalLinks currentPath="/installation-gallery" />
    </div>
  );
};

export default InstallationGalleryPage;
